import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/lib/supabase';

// Analytics data types
export interface InventoryValueSummary {
  totalInventoryValue: number;
  totalIngredients: number;
  averageValuePerIngredient: number;
}

export interface PurchaseCosts {
  totalPurchases: number;
  totalReceipts: number;
  topExpenseIngredient: string | null;
  topExpenseAmount: number;
}

export interface COGSData {
  totalCogs: number;
  totalOrders: number;
  averageCogsPerOrder: number;
}

export interface WasteData {
  totalWasteValue: number;
  shortageValue: number; // Losses
  overageValue: number; // Gains
  totalValidations: number;
}

export interface CostAnalyticsData {
  inventoryValue: InventoryValueSummary;
  purchases: PurchaseCosts;
  cogs: COGSData;
  waste: WasteData;
}

// Period for analytics
export interface AnalyticsPeriod {
  start: Date;
  end: Date;
}

/**
 * Fetch all cost analytics data for a restaurant
 * Calls all 4 analytics functions in parallel
 */
export function useCostAnalytics(
  restaurantId: string,
  period: AnalyticsPeriod
) {
  // Serialize period dates to ISO strings for stable query key
  const queryKey = useMemo(
    () => ['cost-analytics', restaurantId, period.start.toISOString(), period.end.toISOString()],
    [restaurantId, period.start.getTime(), period.end.getTime()]
  );

  return useQuery({
    queryKey,
    queryFn: async () => {
      // Call all 4 analytics functions in parallel
      const [inventoryValueResult, purchasesResult, cogsResult, wasteResult] =
        await Promise.all([
          // 1. Inventory value summary (no date filter)
          supabase.rpc('get_inventory_value_summary', {
            p_restaurant_id: restaurantId,
          }),

          // 2. Purchase costs for period
          supabase.rpc('get_monthly_purchase_costs', {
            p_restaurant_id: restaurantId,
            p_start_date: period.start.toISOString(),
            p_end_date: period.end.toISOString(),
          }),

          // 3. COGS for period
          supabase.rpc('get_cogs_for_period', {
            p_restaurant_id: restaurantId,
            p_start_date: period.start.toISOString(),
            p_end_date: period.end.toISOString(),
          }),

          // 4. Waste/loss for period
          supabase.rpc('get_waste_value_for_period', {
            p_restaurant_id: restaurantId,
            p_start_date: period.start.toISOString(),
            p_end_date: period.end.toISOString(),
          }),
        ]);

      // Check for errors
      if (inventoryValueResult.error) throw inventoryValueResult.error;
      if (purchasesResult.error) throw purchasesResult.error;
      if (cogsResult.error) throw cogsResult.error;
      if (wasteResult.error) throw wasteResult.error;

      // Extract data (functions return single row with snake_case keys from PostgreSQL)
      // Map to camelCase for TypeScript interfaces
      const inventoryValueData = inventoryValueResult.data?.[0];
      const inventoryValue: InventoryValueSummary = {
        totalInventoryValue: inventoryValueData?.total_inventory_value ?? 0,
        totalIngredients: inventoryValueData?.total_ingredients ?? 0,
        averageValuePerIngredient: inventoryValueData?.average_value_per_ingredient ?? 0,
      };

      const purchasesData = purchasesResult.data?.[0];
      const purchases: PurchaseCosts = {
        totalPurchases: purchasesData?.total_purchases ?? 0,
        totalReceipts: purchasesData?.total_receipts ?? 0,
        topExpenseIngredient: purchasesData?.top_expense_ingredient ?? null,
        topExpenseAmount: purchasesData?.top_expense_amount ?? 0,
      };

      const cogsData = cogsResult.data?.[0];
      const cogs: COGSData = {
        totalCogs: cogsData?.total_cogs ?? 0,
        totalOrders: cogsData?.total_orders ?? 0,
        averageCogsPerOrder: cogsData?.average_cogs_per_order ?? 0,
      };

      const wasteData = wasteResult.data?.[0];
      const waste: WasteData = {
        totalWasteValue: wasteData?.total_waste_value ?? 0,
        shortageValue: wasteData?.shortage_value ?? 0,
        overageValue: wasteData?.overage_value ?? 0,
        totalValidations: wasteData?.total_validations ?? 0,
      };

      return {
        inventoryValue,
        purchases,
        cogs,
        waste,
      } as CostAnalyticsData;
    },
    enabled: !!restaurantId,
    staleTime: 2 * 60 * 1000, // 2 minutes - analytics data doesn't change frequently
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
}

/**
 * Helper hook to get common date range presets
 * Memoized to prevent infinite loops
 * Dates are rounded to the hour for stability (prevents millisecond-level changes)
 */
export function useAnalyticsPeriod(preset: 'today' | 'week' | 'month' | 'custom', customRange?: AnalyticsPeriod): AnalyticsPeriod {
  return useMemo(() => {
    const now = new Date();
    // Round to start of today for stability
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    // Round to end of current hour for stability
    const endOfCurrentHour = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() + 1);

    switch (preset) {
      case 'today':
        return {
          start: startOfToday,
          end: endOfCurrentHour,
        };
      case 'week':
        const weekAgo = new Date(now);
        weekAgo.setDate(now.getDate() - 7);
        weekAgo.setHours(0, 0, 0, 0);
        return {
          start: weekAgo,
          end: endOfCurrentHour,
        };
      case 'month':
        const monthAgo = new Date(now);
        monthAgo.setDate(now.getDate() - 30);
        monthAgo.setHours(0, 0, 0, 0);
        return {
          start: monthAgo,
          end: endOfCurrentHour,
        };
      case 'custom':
        if (customRange) {
          // Round custom range dates for stability
          const start = new Date(customRange.start);
          start.setMinutes(0, 0, 0);
          const end = new Date(customRange.end);
          end.setMinutes(59, 59, 999);
          return { start, end };
        }
        return { start: startOfToday, end: endOfCurrentHour };
      default:
        return { start: startOfToday, end: endOfCurrentHour };
    }
    // Only recalculate when preset changes or custom range changes
    // Note: We intentionally don't include 'now' in dependencies to prevent constant recalculation
  }, [preset, customRange?.start?.getTime(), customRange?.end?.getTime()]);
}
